import { Box, Typography, Divider } from '@mui/material';
import { styled } from '@mui/system';
import { CommsHeader, CommsTitle, PatientHeader } from '../communications-widget/styles/communcations-styles';
import CommsPatientCard from '../communications-widget/patient-card';
import { useEffect, useState } from 'react';
import { useConfig, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { useKazoo } from '../../../../hooks/use-kazoo/use-kazoo';
import { Message, VoicemailApi } from '../../../../services/voicemail-service/voicemail-service';
import { useVoicemail } from '../../../../hooks/use-voicemail/use-voicemail';
import AudioPlayer from '../../../shared/audio-player';
import moment from 'moment';
import jwt_decode from 'jwt-decode';
import CallHistoryService from '../../../../services/call-history/call-history-service';
import { VoicemailBox } from '../../../../hooks/use-voicemail/types';
import VoicemailIcon from '@mui/icons-material/Voicemail';

const Container = styled(Box)({
    height: '100%',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column'
});

const AudioPlayerContainer = styled(Box)({
    padding: '16px 16px',
    borderTop: '1px solid var(--grayscale-gs-15, #E1E4E7)'
});

const Info = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    padding: '20px 0px',
    justifyContent: 'space-between'
});

const Title = styled(Typography)({
    color: 'var(--text-primary, #000)',
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '20px',
    letterSpacing: '0.15px'
});

const Data = styled(Typography)({
    color: 'var(--text-secondary, #2C3542)',
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.15px'
});

const Deleted = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: '30px'
});

export interface Status {
    isUnread?: boolean;
    isArchived?: boolean;
    isSaved?: boolean;
}

interface VoicemailWidgetProps {
    patientPhoneNumbers: string[];
    patientId?: number;
    locationId: number;
    callId: string;
}

export default function VoicemailWidget({ patientPhoneNumbers, patientId, locationId, callId }: VoicemailWidgetProps) {
    const { config } = useConfig();
    const { oidcService } = useOidcWithAdmin();
    const { unfilteredBoxes } = useVoicemail();
    const { userCredentials } = useKazoo();
    const voicemailApi = new VoicemailApi(config, oidcService);
    const [voicemailInfo, setVoicemailInfo] = useState<Message>();
    const [mediaUrl, setMediaUrl] = useState<string>('');
    const [mailboxName, setMailboxName] = useState<string>('');
    const [isEnabled, setIsEnabled] = useState<boolean>(null!);
    const callHistoryApi = CallHistoryService.getInstance(config, oidcService);
    const [mailbox, setMailbox] = useState<VoicemailBox>();
    const [isLoading, setIsLoading] = useState<boolean>(null!);

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            const callHistoryResponse = await callHistoryApi.get(callId);

            const voicemailInfo =
                userCredentials &&
                (await voicemailApi.fetchMessage(
                    userCredentials.kazooApiUrl,
                    userCredentials.kazooAccountId,
                    userCredentials.kazooUserToken,
                    callHistoryResponse.voicemailBox,
                    callHistoryResponse.voicemailId
                ));

            const mailbox = unfilteredBoxes.find(box => box.id === callHistoryResponse.voicemailBox);
            const { owner_id: ownerId } = jwt_decode(userCredentials?.kazooUserToken || '') as { owner_id: string };

            setMailbox(mailbox);
            setVoicemailInfo(voicemailInfo ? voicemailInfo : undefined);
            setMailboxName(mailbox?.name || '');
            setIsEnabled(mailbox?.ownerId == ownerId || !mailbox?.ownerId);
            setIsLoading(false);
        })();
    }, [userCredentials, callId]);

    useEffect(() => {
        if (userCredentials && voicemailInfo && mailbox) {
            voicemailApi
                .generateRawAudioUrl(
                    userCredentials.kazooApiUrl,
                    userCredentials.kazooAccountId,
                    userCredentials.kazooUserToken,
                    mailbox?.id,
                    voicemailInfo.media_id
                )
                .then(url => {
                    setMediaUrl(url);
                });
        }
    }, [userCredentials, voicemailInfo, mailbox]);

    return (
        <Container>
            <>
                <PatientHeader>
                    <CommsPatientCard
                        phoneNumbers={patientPhoneNumbers}
                        patientId={patientId}
                        locationId={locationId}
                    />
                </PatientHeader>
                <CommsHeader>
                    <CommsTitle>
                        <Typography variant='body1' color='#000000' sx={{ padding: '10px 20px', fontSize: '1.1rem' }}>
                            Voicemail
                        </Typography>
                    </CommsTitle>
                </CommsHeader>
                <Divider />
            </>
            {mediaUrl && voicemailInfo && (
                <AudioPlayerContainer>
                    <AudioPlayer src={mediaUrl} isEnabled={isEnabled} />
                    <Divider sx={{ padding: '8px' }} />
                    <Info>
                        <Title>Time</Title>
                        <Data>
                            {moment.unix(voicemailInfo.timestamp - 62167219200).format('LT')}
                        </Data>
                    </Info>
                    <Divider />
                    <Info>
                        <Title>Date</Title>
                        <Data>
                            {moment.unix(voicemailInfo.timestamp - 62167219200).format('MM/DD/YYYY')}
                        </Data>
                    </Info>
                    <Divider />
                    <Info>
                        <Title>Mailbox</Title>
                        <Data>{mailboxName}</Data>
                    </Info>
                    <Divider />
                </AudioPlayerContainer>
            )}
            {!mediaUrl && !voicemailInfo && isLoading == false && (
                <Deleted>
                    <VoicemailIcon fontSize={'large'} />
                    <Typography>This voicemail was deleted.</Typography>
                </Deleted>
            )}
        </Container>
    );
}
